/**
 * Thin client-side event tracker for GA4 + Plausible.
 *
 * Guarded: no-op if gtag/plausible are unavailable (SSR, no consent, blocked).
 */

type EventParams = Record<string, string | number | boolean | undefined | null>;

type PlausibleProps = Record<string, string | number | boolean>;

declare global {
  interface Window {
    gtag?: (command: string, ...args: unknown[]) => void;
    dataLayer?: unknown[];
    plausible?: (
      event: string,
      options?: {
        props?: PlausibleProps;
        interactive?: boolean;
        callback?: (result?: unknown) => void;
      },
    ) => void;
  }
}

const DEBUG =
  typeof window !== "undefined" &&
  new URLSearchParams(window.location.search).has("d4_debug");

/**
 * Forward a custom event to Plausible (self-hosted, cookieless: no consent gate).
 * Plausible only accepts flat props: drop null/undefined, keep
 * string/number/boolean, stringify everything else.
 */
// Not user actions: Plausible already measures scroll depth natively, and as
// custom events these would count as engagement and hide real bounces.
const PLAUSIBLE_SKIP = /^(scroll_|experiment_exposure$)/;

export function sendPlausibleEvent(
  name: string,
  params: Record<string, unknown> = {},
): void {
  if (typeof window === "undefined" || !window.plausible) return;
  if (PLAUSIBLE_SKIP.test(name)) return;

  const props: PlausibleProps = {};
  for (const [key, value] of Object.entries(params)) {
    if (value === null || value === undefined) continue;
    if (
      typeof value === "string" ||
      typeof value === "number" ||
      typeof value === "boolean"
    ) {
      props[key] = value;
    } else {
      props[key] =
        typeof value === "object" ? JSON.stringify(value) : String(value);
    }
  }

  window.plausible(
    name,
    Object.keys(props).length > 0 ? { props } : undefined,
  );
}

export function trackEvent(name: string, params: EventParams = {}): void {
  if (typeof window === "undefined") return;

  // Clean out nullish values - GA4 rejects them as "null" strings.
  const clean: Record<string, string | number | boolean> = {};
  for (const [key, value] of Object.entries(params)) {
    if (value === null || value === undefined) continue;
    clean[key] = value;
  }

  if (window.gtag) {
    window.gtag("event", name, clean);
  } else if (window.dataLayer) {
    window.dataLayer.push({ event: name, ...clean });
  }

  sendPlausibleEvent(name, clean);

  if (DEBUG) {
    // eslint-disable-next-line no-console
    console.log("[d4 track]", name, clean);
  }
}

/** Convenience wrappers used by UI components. */
export const track = {
  tidycalClick: (context?: { service?: string; city?: string; source?: string }) =>
    trackEvent("tidycal_click", {
      service: context?.service,
      city: context?.city,
      source: context?.source,
    }),

  emailClick: (source?: string) => trackEvent("email_click", { source }),

  phoneClick: (source?: string) => trackEvent("phone_click", { source }),

  whatsappClick: (context?: { service?: string; city?: string }) =>
    trackEvent("whatsapp_click", {
      service: context?.service,
      city: context?.city,
    }),

  formSubmit: (formId: string) => trackEvent("form_submit", { form_id: formId }),

  scroll: (depth: 25 | 50 | 75 | 90, page?: string) =>
    trackEvent(`scroll_${depth}`, { page }),

  demoPreviewOpen: (context?: { service?: string; city?: string }) =>
    trackEvent("demo_preview_open", {
      service: context?.service,
      city: context?.city,
    }),

  faqExpand: (question: string, page?: string) =>
    trackEvent("faq_expand", { question, page }),

  relatedServiceClick: (fromSlug: string, toSlug: string) =>
    trackEvent("related_service_click", { from: fromSlug, to: toSlug }),

  cityPillClick: (citySlug: string, fromSlug?: string) =>
    trackEvent("city_pill_click", { city: citySlug, from: fromSlug }),

  experimentExposure: (experimentId: string, variant: string) =>
    trackEvent("experiment_exposure", { experiment_id: experimentId, variant }),
};
